import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Box,
  Drawer,
  Toolbar,
  IconButton,
  Container,
  Button,
  ClickAwayListener,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Phone } from "@mui/icons-material";
import styled from "@emotion/styled";
import useSWRMutation from "swr/mutation";
import Footer from "./Footer";
import Profile from "../pages/Profile";
import CallMeDialog from "../pages/CallMeDialog";
import ScriptLoader from "../account/ScriptLoader";
import CallMeDialogLogin from "../pages/CallMeDialogLogin";
import { useNav } from "../../context/NavProvider";
import { useUtils } from "../../context/UtilsProvider";
import { useAuth } from "../../context/AuthProvider";
import { useAccount } from "../../context/AccountProvider";
import { useSfs } from "../../context/SfsProvider";
import { useArrangement } from "../../context/ArrangementProvider";
import { useCallMe } from "../../context/CallMeProvider";
import useAppData from "../../hooks/useAppData";
import { useAnalytics } from "../../context/AnalyticsProvider";
import { ANALYTICS_TAGS } from "../../constants/analyticsTags";

function ResponsiveAppBar() {
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);
  const [openCallback, setOpenCallback] = useState(false);
  const [openCallDialogLogin, setOpenCallDialogLogin] = useState(false);
  const [callbackSet, setCallbackSet] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const menuTimeoutRef = useRef();

  const { useGetAccount } = useAccount();
  const { account } = useGetAccount();
  const { showFooter, showCallMeIcon, hideSignOut, hideHeaderNavBars } = useNav();
  const { accessToken, endSession, postSignOut, authStates, setAuthState } = useAuth();
  const { clientConfig, disableProfile } = useAppData();
  const { setSfsLoaded, disposableIncome, useGetSfs, monthsSinceReview } = useSfs();
  const { sfs } = useGetSfs();
  const { setLastSavedPlan, setPlanLoaded, hasArrangement, lastSavedPlan } = useArrangement();
  const { createAlert, alertSeverityTypes } = useUtils();
  const navigate = useNavigate();
  const { trigger } = useSWRMutation("/auth/signout", postSignOut);
  const callMeActive = !!clientConfig?.layout?.callMe?.active;
  const { getCallbacks } = useCallMe();
  const { setTag } = useAnalytics();
  const location = useLocation();

  const AppBarStyle = styled("div")(({ theme }) => ({
    [theme.breakpoints.down("md")]: {
      color: clientConfig.layout.header.mobileColour,
      backgroundColor: clientConfig.layout.header.mobileBackgroundColour
    },
    [theme.breakpoints.up("md")]: {
      color: clientConfig.layout.header.colour,
      backgroundColor: clientConfig.layout.header.backgroundColour
    }
  }));

  const showAccountMenu = clientConfig?.layout?.navigation?.manageMyAccount?.display !== false;
  const showInfoMenu = clientConfig?.layout?.navigation?.infoAndSupport?.display !== false;

  const getVisibleItems = (items = []) =>
    items.filter(item => item.display !== false).map(item => ({
      name: item.label,
      href: item.url || '#',
    }));

  const baseAccountItems = getVisibleItems(clientConfig?.layout?.navigation?.manageMyAccount?.subCategories);
  const infoItems = getVisibleItems(clientConfig?.layout?.navigation?.infoAndSupport?.subCategories);

  const createAmendPlanLabel = "Create or amend repayment plan";
  const hasValidSfs = sfs && monthsSinceReview <= 6;
  const createAmendPlanHref = (hasValidSfs && disposableIncome > 1) ? "/repayment" : "/sfs";

  const filteredAccountItems = useMemo(() => {
    return baseAccountItems
    .filter(item => {
      const isCreateAmendItem = item.name === createAmendPlanLabel;
      if (!isCreateAmendItem) return true;

      return (
        lastSavedPlan &&
        !lastSavedPlan.inPaymentWindow &&
        !lastSavedPlan.isSettlement &&
        hasArrangement
      );
    })
    .map(item => {
      if (item.name === createAmendPlanLabel) {
        return { ...item, href: createAmendPlanHref };
      }
      return item;
    })
  }, [baseAccountItems, lastSavedPlan, hasArrangement]);

  const menus = {
    account: filteredAccountItems,
    info: infoItems,
  };

  const handleOpenCallback = () => setOpenCallback(true);
  const handleCloseCallback = () => setOpenCallback(false);
  const handleCloseCallbackLogin = () => setOpenCallDialogLogin(false);
  const handleOpenCallbackLogin = () => setOpenCallDialogLogin(true);

  const handleCallbackSet = async (ref, forceSet = false) => {
    if (!callMeActive) {
      setCallbackSet(false);
      return;
    }
    if (forceSet) return setCallbackSet(true);
    try {
      const response = await getCallbacks(ref);
      const now = new Date();
      const futureCallbacks = response?.callbacks?.filter(cb => new Date(cb.requestedCallbackDatetime) > now) || [];
      setCallbackSet(futureCallbacks.length > 0);
    } catch {
      setCallbackSet(false);
    }
  };

  useEffect(() => {
    if (!callMeActive) return;
    const checkStatus = async () => {
      if (!accessToken || !account?.accountReference) return;
      await handleCallbackSet(account.accountReference);
    };
    checkStatus();
    const interval = setInterval(checkStatus, 60000);
    return () => clearInterval(interval);
  }, [callMeActive, accessToken, account?.accountReference]);

  const handleMouseEnter = useCallback((menuKey) => {
    clearTimeout(menuTimeoutRef.current);
    setOpenMenu(menuKey);
  }, []);

  const handleMouseLeave = useCallback(() => {
    menuTimeoutRef.current = setTimeout(() => {
      setOpenMenu(null);
    }, 200);
  }, []);

  const toggleMenu = (menuKey) => {
    clearTimeout(menuTimeoutRef.current);
    setOpenMenu(prev => (prev === menuKey ? null : menuKey));
  };

  const toggleProfileDrawer = (open) => () => {
    setProfileDrawerOpen(open);
  };

  const closeMenu = () => {
    clearTimeout(menuTimeoutRef.current);
    setOpenMenu(null);
  };

  const handleClick = () => (window.location.href = "/");

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleSignOut = async (event) => {
    event.preventDefault();

    setTag(ANALYTICS_TAGS.LOGGED_OUT_SUCCESSFUL, { path: location.pathname })

    await trigger("");
    setSfsLoaded(false);
    setPlanLoaded(false);
    setLastSavedPlan(null);
    setProfileDrawerOpen(false);
    endSession();
    setAuthState(authStates.signedOut);
    createAlert("User sign out successful", alertSeverityTypes.success);
    navigate("/auth");
  };

  return clientConfig && (
    <>
      <ScriptLoader />
      <AppBar position="static">
        <AppBarStyle>
          <Container maxWidth='xl'>
            <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {accessToken && !hideHeaderNavBars && <IconButton sx={{ display: { md: 'none' } }} onClick={toggleMobileMenu}>
                  {mobileMenuOpen ? (
                    <CloseIcon sx={{ color: '#fff', fontSize: '2rem' }} />
                  ) : (
                      <MenuIcon sx={{ color: 'primary.dark', fontSize: '2rem' }} />
                  )}
                </IconButton>}
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: {
                      xs: 'center',   // center by default
                    },
                    pl: {
                      xs: 2,
                      sm: 0,
                    },
                    '@media (max-width: 399px)': {
                      justifyContent: 'flex-start', // left-align on very small phones
                    },
                    '@media (min-width:960px)': {
                      justifyContent: 'flex-start', // left-align on desktop
                    },
                  }}
                >
                  <Link onClick={handleClick}>
                    <Box
                      component="img"
                      src={clientConfig.layout.header.logo}
                      alt="Logo"
                      sx={{ display: { xs: 'none', md: 'block' }, width: 130 }}
                    />
                    <Box
                      component="img"
                      src={clientConfig.layout.header.logoMobile}
                      alt="Logo"
                      sx={{ display: { xs: 'block', md: 'none' }, width: 130 }}
                    />
                  </Link>
                </Box>

                {accessToken && !hideHeaderNavBars && (
                  <Box sx={{ display: { xs: 'none', md: 'flex' }, ml: 5 }}>
                    {[
                      ...(showAccountMenu ? ['account'] : []),
                      ...(showInfoMenu ? ['info'] : [])
                    ].map((menuKey, idx) => (
                      <ClickAwayListener onClickAway={closeMenu} key={menuKey}>
                        <Box
                          onMouseEnter={() => handleMouseEnter(menuKey)}
                          onMouseLeave={handleMouseLeave}
                          sx={{ display: 'flex', flexDirection: 'column', position: 'relative' }}
                        >
                          <Button
                            onClick={() => toggleMenu(menuKey)}
                            sx={{
                              ml: idx > 0 ? 4 : 0,
                              color: '#000',
                              fontWeight: 'bold',
                              textTransform: 'none',
                              '&:hover': { backgroundColor: '#f5f5f5' },
                              fontSize: '16px',
                              height: '64px',
                              display: 'flex',
                              alignItems: 'center',
                              px: 2
                            }}
                          >
                            {menuKey === 'account' ? 'Manage my account' : 'Info & Support'}
                          </Button>
                          {openMenu === menuKey && (
                            <Box
                              sx={{
                                display: 'flex',
                                backgroundColor: '#f5f5f5',
                                px: 2,
                                py: 1,
                                top: '64px',
                                position: 'fixed',
                                left: 0,
                                right: 0,
                                zIndex: 1,
                                width: '100vw',
                                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
                                justifyContent: 'center',
                              }}
                            >
                              {menus[menuKey].map((item, idx) => (
                                <Link
                                  key={item.href}
                                  to={item.href}
                                  onClick={closeMenu}
                                  style={{
                                    padding: '8px 16px',
                                    textDecoration: 'none',
                                    color: '#000',
                                    fontSize: '14px',
                                    whiteSpace: 'nowrap',
                                    textAlign: 'center',
                                    borderLeft: idx !== 0 ? '1px solid #ddd' : 'none',
                                  }}
                                >
                                  {item.name}
                                </Link>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </ClickAwayListener>
                    ))}
                  </Box>
                )}
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", ml: "auto" }}>
                {accessToken && clientConfig?.layout.callMe.active && showCallMeIcon && (
                  <IconButton onClick={handleOpenCallback}>
                    <Phone sx={{ fontSize: "2rem", color: "secondary.contrastText" }} />
                    {callbackSet && (
                      <AccessTimeIcon sx={{
                        fontSize: "1rem",
                        marginBottom: "15px",
                        marginLeft: "20px",
                        position: "absolute",
                        color: "secondary.contrastText"
                      }} />
                    )}
                  </IconButton>
                )}

                {accessToken && (
                  (disableProfile?.required || !clientConfig.layout.header.profileManager.active) ? (!hideSignOut && (
                    <Button
                      id='logout'
                      onClick={handleSignOut}
                      variant='outlined'
                      sx={{
                        borderRadius: 0,
                        textTransform: "none",
                        ml: 2,
                        borderColor: "primary.dark",
                        color: "primary.dark",
                        "&:hover": {
                          borderColor: "primary.light",
                        },
                      }}
                    >
                      Log out
                    </Button>
                  )) : (
                    <IconButton
                      edge="end"
                      color="inherit"
                      onClick={toggleProfileDrawer(true)}
                      sx={{
                        fontSize: "2rem",
                        color: "secondary.contrastText"
                      }}
                    >
                      <PersonOutlineOutlinedIcon sx={{ fontSize: "2rem" }} />
                    </IconButton>
                  )
                )}

                {!accessToken && clientConfig?.layout.callMe.active && (
                  <Box sx={{ display: "flex", position: "relative", justifyContent: "flex-end", width: "100%" }}>
                    <IconButton onClick={handleOpenCallbackLogin} >
                      <Phone
                        sx={{
                          fontSize: "2rem",
                          color: "secondary.contrastText"
                        }}
                      />
                    </IconButton>
                    {callbackSet && (
                      <AccessTimeIcon
                        sx={{
                          fontSize: "1rem",
                          marginBottom: "15px",
                          marginLeft: "20px",
                          position: "absolute",
                          color: "secondary.contrastText"
                        }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Toolbar>

          </Container>
        </AppBarStyle>
      </AppBar>

      {/* Mobile Burger Menu Drawer */}
      {accessToken && !hideHeaderNavBars && <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          width: '100vw',
          '& .MuiDrawer-paper': {
            width: '100%',
            maxWidth: '100vw',
            backgroundColor: 'primary.dark',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
          },
        }}
      >
        {/* Close Button at the Top Left */}
        {accessToken && !hideHeaderNavBars && <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => setMobileMenuOpen(false)} sx={{ color: '#fff', padding: '8px', mb: 1, ml: 2, mt: 1 }}>
            <CloseIcon sx={{ fontSize: '2rem' }} />
          </IconButton>
        </Box>}

        {/* Menu Content Directly Below */}
        {accessToken && !hideHeaderNavBars && <Box>
          {[
            ...(showAccountMenu ? [{ label: "Manage my account", key: "account" }] : []),
            ...(showInfoMenu ? [{ label: "Info & Support", key: "info" }] : [])
          ].map(({ label, key }) => (
            <Accordion
              key={key}
              expanded={expandedSection === key}
              onChange={() => setExpandedSection(expandedSection === key ? null : key)}
              sx={{
                backgroundColor: 'primary.dark',
                boxShadow: 'none',
                borderRadius: 0,
                margin: 0,
                padding: 0,
                '&.Mui-expanded': { margin: 0 }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon style={{ color: '#fff' }} />}
                sx={{
                  color: '#fff',
                  minHeight: '48px',
                  height: '48px',
                  paddingLeft: '16px',
                  paddingRight: '16px'
                }}
              >
                <Typography>{label}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ backgroundColor: 'secondary.light', padding: 0, margin: 0 }}>
                {menus[key].map(item => (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    style={{ textDecoration: 'none' }}
                  >
                    <Typography sx={{
                      p: 1,
                      pl: 2,
                      pr: 2,
                      borderBottom: '1px solid #fff',
                      color: "primary.contrastText",
                      width: '100%'
                    }}>
                      {item.name}
                    </Typography>
                  </Link>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>}
      </Drawer>}

      {accessToken && clientConfig?.layout.callMe.active && <CallMeDialog open={openCallback} onClose={handleCloseCallback} onCallbackSet={handleCallbackSet} />}
      {!accessToken && clientConfig?.layout.callMe.active && <CallMeDialogLogin open={openCallDialogLogin} onClose={handleCloseCallbackLogin} onCallbackSet={handleCallbackSet} />}
      {accessToken && <Drawer anchor="right" open={profileDrawerOpen} onClose={() => setProfileDrawerOpen(false)} sx={{ '& .MuiDrawer-paper': { width: '85%', maxWidth: 450 } }}><Profile /></Drawer>}
      <Outlet />
      {showFooter && <Footer />}
    </>
  );
}

export default ResponsiveAppBar;
